import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter as Router, Link, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { BackendStatusProvider, useBackendStatus } from 'context/BackendStatusContext';
import { useIsAdmin } from 'hooks/useIsAdmin';
import { useMediaQuery } from 'hooks/useMediaQuery';
import { EVENTO_SESSAO_EXPIRADA } from 'services/api';
import { lerToken, temSessao } from 'services/sessao';
import { ehNativo } from 'services/plataforma';
import { useBotaoVoltarNativo } from 'hooks/useBotaoVoltarNativo';
import { usePushNativo } from 'hooks/usePushNativo';
import usePresenca from 'hooks/usePresenca';
import {
    EVENTO_CONSENTIMENTO,
    iniciarAnalytics,
    registrarPageview,
    temConsentimento,
} from 'services/analytics';

import NavBar 			from 'components/NavBar';
import Icon 			from 'components/Icon';
import Footer 			from 'components/Footer';
import CookieBanner from 'components/CookieBanner';
import ConsentimentoApp from 'components/ConsentimentoApp';
import AvisoSemConexao from 'components/AvisoSemConexao';
import SplashApp from 'components/SplashApp';
import AvisoVersaoApp from 'components/AvisoVersaoApp';

/*
 * ── O que é carregado junto com o site, e o que só quando alguém vai lá ──────────────────────
 *
 * Até aqui as 26 telas entravam no mesmo arquivo. Quem abria a Home baixava, sem nunca ver, o
 * administrativo inteiro -- e no app era pior do que peso: a área `/admin` nem existe para quem usa o
 * aplicativo (é acesso por lista de ids, um único administrador hoje, e só no computador), e ainda
 * assim ia dentro do pacote instalado no aparelho de todo mundo. Hoje o build do app sai sem ela
 * (ver `telasAdmin`, abaixo).
 *
 * As quatro de cima ficam no pacote inicial de propósito, cada uma por um motivo:
 *
 *  - `Home` é a primeira tela da WEB e a mais visitada; adiar o que a pessoa já está esperando ver
 *    só troca peso por um piscar de "Carregando…". Continua eager mesmo depois de o app deixar de
 *    abrir nela (ver RaizDoApp): o build é o mesmo dos dois lados, e na web ela é o LCP;
 *  - `Login` é a porta de tudo e é pequena;
 *  - `Manutencao` e `NaoEncontrada` são as telas de quando algo deu errado -- e é justamente aí que
 *    buscar um pedaço do site pela rede tem mais chance de falhar. Uma tela de erro que depende de
 *    download para aparecer não é uma tela de erro.
 *
 * Todo o resto é `lazy`: vira um arquivo separado, baixado na hora em que a rota é aberta.
 */
import Home			 	from 'pages/Home';
import Login 			from 'pages/Login';
import Manutencao 		from 'pages/Manutencao';
import NaoEncontrada	from 'pages/NaoEncontrada';

const RecuperarSenha 	= lazy(() => import('pages/RecuperarSenha'));
const NovaSenha 		= lazy(() => import('pages/NovaSenha'));
const Dashboard 		= lazy(() => import('pages/Dashboard'));
const Agenda 			= lazy(() => import('pages/Agenda'));
const Agendar 			= lazy(() => import('pages/Agendar'));
const Profissional 		= lazy(() => import('pages/Profissional'));
const AtivarPerfilProfissional = lazy(() => import('pages/Profissional/Ativar'));
const UsuarioNovo 		= lazy(() => import('pages/Usuario/Novo'));
const UsuarioEditar 	= lazy(() => import('pages/Usuario/Editar'));
const UsuarioDespedida 	= lazy(() => import('pages/Usuario/Despedida'));
const PagamentoAssinatura = lazy(() => import('pages/Assinatura/PagamentoAssinatura'));
const Verificacao		= lazy(() => import('pages/Verificacao'));
const QuemSomos 		= lazy(() => import('pages/Institucional/QuemSomos'));
const PerguntasFrequentes = lazy(() => import('pages/Faq'));
const Ajuda				= lazy(() => import('pages/Ajuda'));
const ArtigoAjuda		= lazy(() => import('pages/Ajuda/Artigo'));
const PoliticaCookies 	= lazy(() => import('pages/Institucional/PoliticaCookies'));
const PoliticaPrivacidade = lazy(() => import('pages/Institucional/PoliticaPrivacidade'));
const TermosUso 		= lazy(() => import('pages/Institucional/TermosUso'));
const ExclusaoDeConta	= lazy(() => import('pages/Institucional/ExclusaoDeConta'));
const Cidade			= lazy(() => import('pages/Cidade'));
const Diaristas			= lazy(() => import('pages/Diaristas'));

/*
 * Administrativo. Uma seção por arquivo, e não um pacote só de `/admin`: o shell é leve e quem abre
 * o financeiro não precisa do relatório de usuários junto.
 *
 * O build do app (`build:native`, com REACT_APP_COM_ADMIN=false) sai SEM nenhuma delas. O `lazy`
 * sozinho não bastava: ele só adia o download, e o `cap sync` copia a pasta build/ inteira -- os
 * chunks do admin iam para o aparelho de todo mundo sem nunca abrir. Com a variável em 'false', o
 * webpack (ConstPlugin) resolve o ternário no build e não percorre o ramo morto: os `import()` lá
 * dentro não viram chunk.
 *
 * Por isso a condição é `process.env.REACT_APP_COM_ADMIN` escrito AQUI, literal. Guardada antes
 * numa constante, o webpack não sabe o valor; quem apagaria o ramo seria o Terser, que roda depois
 * de os chunks já terem sido gerados. Sem a variável (web, `npm start`, Jest) o admin entra.
 */
const telasAdmin = process.env.REACT_APP_COM_ADMIN === 'false' ? null : {
    Admin: 					lazy(() => import('pages/Admin')),
    Painel: 				lazy(() => import('pages/Admin/Painel')),
    Metas:					lazy(() => import('pages/Admin/Metas')),
    Usuarios:				lazy(() => import('pages/Admin/Usuarios')),
    UsuariosMapa:			lazy(() => import('pages/Admin/Usuarios/Mapa')),
    Servicos:				lazy(() => import('pages/Admin/Servicos')),
    Demanda:				lazy(() => import('pages/Admin/Demanda')),
    Verificacoes:			lazy(() => import('pages/Admin/Verificacoes')),
    Ocorrencias:			lazy(() => import('pages/Admin/Ocorrencias')),
    Agendamentos:			lazy(() => import('pages/Admin/Agendamentos')),
    Financeiro:				lazy(() => import('pages/Admin/Financeiro')),
    Planos:					lazy(() => import('pages/Admin/Financeiro/Planos')),
    Extrato:				lazy(() => import('pages/Admin/Financeiro/Extrato')),
    Configuracoes:			lazy(() => import('pages/Admin/Configuracoes')),
    ConfiguracoesGerais:	lazy(() => import('pages/Admin/Configuracoes/Gerais')),
    RegrasDosPlanos:		lazy(() => import('pages/Admin/Configuracoes/RegrasDosPlanos')),
    Fundadores:				lazy(() => import('pages/Admin/Configuracoes/Fundadores')),
    Health:					lazy(() => import('pages/Admin/Health')),
    HealthLog:				lazy(() => import('pages/Admin/Health/Log')),
    HealthJobs:				lazy(() => import('pages/Admin/Health/Jobs')),
};

const SCROLL_TO_TOP_ROUTES = new Set([
    // A Home é destino do "Início" do menu sanduíche a partir de qualquer página. Sem ela aqui, ao
    // fechar o menu a pilha de overlays devolve a rolagem que salvou ao abrir (services/overlays),
    // e a Home aparecia na altura em que a página anterior estava — a pessoa via o meio da landing
    // sem ter rolado nada. A ordem que faz isso funcionar é a do React: a limpeza do efeito do
    // menu (que devolve a rolagem) roda antes do efeito abaixo, que então leva ao topo.
    '/',
    '/login',
    '/esqueciminhasenha',
    '/dashboard',
    '/usuario/novo',
    '/quemsomos',
    '/perguntas-frequentes',
    '/ajuda',
    '/termos-de-uso',
    '/politica-de-cookies',
    '/politica-de-privacidade',
    '/excluir-conta',
    '/admin/painel',
    '/admin/metas',
    '/admin/usuarios',
    '/admin/usuarios/mapa',
    '/admin/servicos',
    '/admin/demanda',
    '/admin/verificacoes',
    '/admin/ocorrencias',
    '/admin/agendamentos',
    '/admin/financeiro/planos',
    '/admin/financeiro/extrato',
    '/admin/configuracoes/gerais',
    '/admin/configuracoes/regras-dos-planos',
    '/admin/configuracoes/fundadores',
    '/admin/health',
    '/admin/health/log',
    '/profissional/ativar',
]);

// Fluxos focados (login, cadastro, pagamento): rodapé em nenhuma largura.
const ROTAS_SEM_RODAPE = [
    '/login',
    '/usuario/novo',
    '/profissional/ativar',
    '/assinatura/pagamento',
    '/verificacao/documento',
    '/usuario/despedida',
    '/esqueciminhasenha',
    '/novasenha',
];

const ScrollToTopOnRouteChange = () => {
    const { pathname, hash } = useLocation();

    useEffect(() => {
        if (hash) {
            const el = document.getElementById(hash.slice(1));
            if (el) {
                el.scrollIntoView({ behavior: 'auto', block: 'start' });
                return;
            }
        }
        // Os artigos da Central de ajuda (/ajuda/<slug>) vão pelo prefixo: um por slug na lista
        // seria uma linha a lembrar a cada artigo novo.
        if (SCROLL_TO_TOP_ROUTES.has(pathname) || pathname.startsWith('/ajuda/')) {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
    }, [pathname, hash]);

    return null;
};

/**
 * Medição de audiência (Google Analytics 4). Não desenha nada.
 *
 * Fica DEPOIS do early return de manutenção em AppRoutes, de propósito: o site fora do ar não é
 * visita, e contá-lo transformaria uma janela de deploy em pico de tráfego no relatório.
 *
 * O efeito depende de `consentiu` além da rota. É o que resolve os três casos com um caminho só:
 * quem já tinha aceitado antes (consentiu nasce true -> inicia e mede a tela de entrada), quem
 * aceita durante a visita (false -> true dispara o efeito na tela em que a pessoa está) e quem
 * navega depois (a rota muda e só o pageview é reenviado, porque iniciarAnalytics é idempotente).
 * Nenhum deles conta duas vezes: o React só reexecuta o efeito quando a tupla de dependências
 * muda de fato.
 */
const Analytics = () => {
    const { pathname, search } = useLocation();
    const [consentiu, setConsentiu] = useState(temConsentimento);

    useEffect(() => {
        // Relê a decisão em vez de assumir "aceitou": desde que existe recusa, este evento significa
        // "mudou". Assumir true faria uma revogação nas configurações LIGAR a medição.
        const aoDecidir = () => setConsentiu(temConsentimento());
        window.addEventListener(EVENTO_CONSENTIMENTO, aoDecidir);
        return () => window.removeEventListener(EVENTO_CONSENTIMENTO, aoDecidir);
    }, []);

    useEffect(() => {
        if (!consentiu) return;
        iniciarAnalytics();
        registrarPageview(`${pathname}${search}`);
    }, [consentiu, pathname, search]);

    return null;
};

/**
 * Leva para /login quando o interceptor anuncia que a sessão expirou. Não desenha nada.
 *
 * A navegação mora aqui, e não em services/api.js, porque o interceptor vive fora da árvore de
 * componentes e não alcança o `navigate`. Antes ele resolvia isso com `window.location.href`, que
 * recarregava o bundle inteiro e descartava todo o estado do React — e, no app nativo, dependeria de
 * um fallback de history que o servidor estático do Capacitor não tem.
 *
 * Dois ganhos que vêm de brinde na web, além de não recarregar:
 *  - a rota de origem é preservada em `redirecionarPara`, do mesmo jeito que o PrivateRoute abaixo
 *    já fazia. Com `window.location.href` ela se perdia, e quem tinha a sessão vencida no meio da
 *    agenda voltava do login para a Home;
 *  - `replace`, e não push: a tela cuja sessão acabou de expirar não pode ficar no histórico como
 *    destino do botão voltar, porque voltar para ela só produziria outro 401.
 *
 * A rota é lida por ref e não por dependência do efeito: assinar `location` re-registraria o
 * listener a cada navegação do site inteiro, para um evento que dispara quase nunca.
 */
const RedirecionarAoExpirarSessao = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const localizacaoAtual = useRef(location);
    localizacaoAtual.current = location;

    useEffect(() => {
        const aoExpirar = () => {
            const { pathname, search } = localizacaoAtual.current;
            // Quem já está no /login não tem para onde ser levado, e registrar o próprio /login como
            // destino de retorno faria o formulário devolver a pessoa a ele depois de entrar.
            if (pathname === '/login') return;
            navigate('/login', { replace: true, state: { redirecionarPara: `${pathname}${search}` } });
        };
        window.addEventListener(EVENTO_SESSAO_EXPIRADA, aoExpirar);
        return () => window.removeEventListener(EVENTO_SESSAO_EXPIRADA, aoExpirar);
    }, [navigate]);

    return null;
};

// NO ESCOPO DO MÓDULO, e não dentro de AppRoutes: componente declarado dentro de outro ganha
// identidade nova a cada render do pai, e o React lê identidade nova como componente diferente —
// em vez de atualizar a subárvore, ele a desmonta e monta outra. Como AppRoutes assina a
// localização (useLocation abaixo), QUALQUER mudança de URL o re-renderiza, e com PrivateRoute lá
// dentro isso remontava a página inteira a cada mudança de querystring: a busca de profissionais
// perdia lista, página e data escolhida e refazia tudo só porque o painel de detalhes abriu.
const PrivateRoute = ({ children }) => {
    const location = useLocation();
    return lerToken()
        ? children
        // Com a querystring: o toque numa notificação abre o app em /agenda?agendamento=ID, e sem
        // sessão o parâmetro precisa sobreviver ao login para o sheet do agendamento abrir depois.
        : <Navigate to="/login" state={{ redirecionarPara: `${location.pathname}${location.search}` }} />;
};

/**
 * Rota da área administrativa. Aqui, e não no PrivateRoute, porque as duas perguntas são
 * diferentes: "está logado?" se responde lendo o localStorage, "é administrador?" só o backend
 * responde (app.admin.usuario-ids), e a resposta demora uma requisição.
 *
 * Este componente é conveniência de navegação, não segurança: quem protege os dados é o
 * AdminAreaInterceptor no backend. Esconder a tela sem barrar o endpoint deixaria qualquer pessoa
 * logada baixar a base inteira pela URL da API -- que era exatamente a situação de /usuario/relatorio.
 *
 * Enquanto useIsAdmin devolve null a tela fica em carregamento: mandar embora antes da resposta
 * expulsaria o próprio administrador a cada F5.
 *
 * `'indisponivel'` (o backend não respondeu, ou respondeu 5xx) manda para o dashboard como o
 * `false`, com UMA exceção: /admin/health. É a tela que existe para o caso de a API estar fora, e
 * barrá-la nesse caso seria barrá-la na única hora em que importa. Pelo mesmo motivo ela abre até
 * SEM token: com a API caída não há como fazer login, e o primeiro teste manual caiu exatamente
 * nisso — janela sem sessão, backend parado, tela de login. Sem backend ela não renderiza dado
 * nenhum — só "API fora do ar" e a versão do front —, e assim que a API volta, GET /admin/health
 * passa pelo AdminAreaInterceptor como qualquer outra rota. A barreira continua sendo o backend.
 */
const AdminRoute = ({ children }) => {
    const location = useLocation();
    const token = lerToken();

    const userId = useMemo(() => {
        if (!token) return null;
        try {
            return jwtDecode(token).userId;
        } catch (error) {
            return null;
        }
    }, [token]);

    const isAdmin = useIsAdmin(userId);
    // Escrita como max-width de propósito: sem matchMedia (Jest) o hook devolve false, e false aqui
    // é "pode mostrar", o comportamento de sempre.
    const telaEstreita = useMediaQuery(LARGURA_ESTREITA_ADMIN);

    // A Saúde é a tela para quando a API caiu — e aí não há como logar. Sem sessão ela só sabe
    // dizer se a API responde; o relatório (GET /admin/health) continua exigindo JWT + admin no
    // backend, e a própria página pede login quando descobre que a API está no ar.
    if (location.pathname === '/admin/health' && !token) {
        return children;
    }

    if (!token) {
        return <Navigate to="/login" state={{ redirecionarPara: `${location.pathname}${location.search}` }} />;
    }

    if (isAdmin === null) {
        return (
            <div className="page-shell min-h-screen flex items-center justify-center" aria-busy="true">
                <p className="text-muted text-sm">Carregando…</p>
            </div>
        );
    }

    if (isAdmin === 'indisponivel') {
        return location.pathname === '/admin/health' ? children : <Navigate to="/dashboard" replace />;
    }

    if (isAdmin !== true) return <Navigate to="/dashboard" replace />;

    // Depois da permissão, e não antes: para quem não é admin o destino continua sendo o dashboard,
    // sem anunciar que existe uma área ali. A Saúde fica de fora, pela mesma razão das exceções
    // acima: é a tela de conferir uma queda, e o celular pode ser o que está à mão na hora.
    if (telaEstreita && location.pathname !== '/admin/health') return <AvisoAdminSoNoComputador />;

    return children;
};

/**
 * O administrativo é só para o computador: as tabelas e os filtros não cabem no celular, e quem
 * usa é uma pessoa só. Abaixo do `lg` (1024px) a rota mostra este aviso no lugar da tela, e não um
 * redirecionamento: girar o tablet ou alargar a janela traz o admin de volta sem recarregar (o
 * useMediaQuery acompanha a mudança). No app o admin nem existe (ver `telasAdmin`).
 */
const LARGURA_ESTREITA_ADMIN = '(max-width: 1023px)';

const AvisoAdminSoNoComputador = () => (
    <div className="page-shell min-h-screen">
        <NavBar />

        <div className="flex items-start justify-center px-4 pb-12 pt-24 sm:pt-32">
            <div className="card-page w-full max-w-lg text-center">
                <div className="icone-circulo mx-auto h-20 w-20">
                    <Icon name="build" className="h-9 w-9" />
                </div>

                <h1 className="t-pagina mt-6">O administrativo é feito para computador</h1>

                <p className="mt-3 text-base text-muted">
                    As telas têm tabelas e filtros que não cabem aqui. Abra numa tela com pelo menos
                    1024 pixels de largura.
                </p>

                <div className="mt-8 flex justify-center">
                    <Link to="/dashboard" className="btn-primary justify-center">
                        Ir para o painel
                    </Link>
                </div>
            </div>
        </div>
    </div>
);

/**
 * A rota "/": tela na web, decisão no app.
 *
 * <h2>Por que o app não pode abrir na Home</h2>
 * A Home é uma landing: hero "contratar x trabalhar", como funciona, vantagens, FAQ, fechamento.
 * Ela existe para convencer quem chegou pelo Google — e quem abre o app já foi convencido. A página
 * da Play Store é a landing do app (ícone, screenshots, descrição, preço), e reapresentar o produto
 * na abertura gasta o momento de maior intenção que existe pedindo rolagem antes de qualquer coisa
 * útil. Nenhum app de referência abre numa página institucional: ou abre no conteúdo (iFood,
 * Airbnb — navegar como visitante, login só na hora de agir), ou abre na autenticação (bancos, apps
 * de conta). Aqui a busca está atrás do PrivateRoute, então a porta é a autenticação.
 *
 * <h2>Por que a decisão mora na rota, e não nos botões</h2>
 * Existe uma dúzia de `Link to="/"` e `navigate('/')` espalhados — o logo das telas de funil, o
 * "voltar ao início" do 404, o logout do NavBar, o cancelar do cadastro. Decidindo aqui, todos
 * acertam de uma vez, e a próxima ocorrência que alguém escrever já nasce certa. A alternativa era
 * caçar ocorrência por ocorrência e deixar a regra espalhada em uma dúzia de lugares.
 *
 * <h2>Os dois detalhes que fazem funcionar</h2>
 * As duas perguntas são SÍNCRONAS de propósito (`ehNativo` lê a runtime, `temSessao` lê o
 * localStorage): a decisão sai no primeiro render, então não há lampejo da Home antes do desvio.
 *
 * E `replace`, não push: "/" não pode ficar no histórico da WebView. Com ele lá, o botão voltar do
 * Android volta para "/", que redireciona de novo — laço sem saída dentro do app.
 *
 * No escopo do módulo pelo mesmo motivo do PrivateRoute acima.
 */
const RaizDoApp = () => {
    if (!ehNativo()) return <Home />;

    return <Navigate to={temSessao() ? '/dashboard' : '/login'} replace />;
};

/**
 * O que aparece enquanto o arquivo da rota está sendo baixado.
 *
 * Ocupa a altura da tela inteira de propósito. Um fallback de altura zero faria a página encolher
 * até o rodapé subir e, um instante depois, voltar ao lugar -- o salto é mais perceptível do que a
 * espera que ele deveria disfarçar.
 *
 * O texto é o mesmo do AdminRoute, que já esperava a permissão chegar antes de decidir: as duas
 * esperas são a mesma coisa para quem olha, e mostrar dois estados de carregamento diferentes na
 * mesma navegação faria parecer que a tela travou duas vezes.
 */
const TelaCarregandoRota = () => (
    <div className="page-shell min-h-screen flex items-center justify-center" aria-busy="true">
        <p className="text-muted text-sm">Carregando…</p>
    </div>
);

export const AppRoutes = () => {
    const { isDown } = useBackendStatus();
    const { pathname } = useLocation();

    // Precisa estar DENTRO do <Router> (usa navigate) e antes do early return de manutenção, para o
    // botão voltar continuar respondendo mesmo com o site fora do ar — sem ele, ali, o único jeito
    // de sair da tela de manutenção seria matar o app.
    useBotaoVoltarNativo();
    // Mesma exigência: dentro do <Router> (o toque na notificação navega).
    usePushNativo();
    // Presença com a tela visível (C7): o sinal de atividade da regra de inatividade. `temSessao` é lido a
    // cada render, como no usePushNativo: o login termina com uma navegação, e aí o efeito liga.
    usePresenca(temSessao());

    if (process.env.REACT_APP_MAINTENANCE_MODE === 'true' || isDown) {
        return <Manutencao />;
    }

    const semRodape = ehNativo() || ROTAS_SEM_RODAPE.includes(pathname) || pathname.startsWith('/admin');
    // A exceção do app: ver a nota no bloco do rodapé, mais abaixo.
    const rodapeDoLoginNoApp = ehNativo() && pathname === '/login';

    return (
        <>
            <ScrollToTopOnRouteChange />
            <Analytics />
            <RedirecionarAoExpirarSessao />

            <Suspense fallback={<TelaCarregandoRota />}>
            <Routes>
                <Route path="/"			element={<RaizDoApp />} />

                <Route path="/login"	element={<Login />} />
                <Route path="/esqueciminhasenha"	element={<RecuperarSenha />} />
                <Route path="/novasenha"	element={<NovaSenha />} />
                <Route path="/agenda"	element={
                    <PrivateRoute>
                        <Agenda />
                    </PrivateRoute>
                } />

                <Route
                    path="/agendar"
                    element={
                        <PrivateRoute>
                            <Agendar />
                        </PrivateRoute>
                    }
                />

                <Route path="/agendar/:id"	element={
                    <PrivateRoute>
                        <Agendar />
                    </PrivateRoute>
                    }
                />

                <Route path="/profissional"	element={
                    <PrivateRoute>
                        <Profissional />
                    </PrivateRoute>
                } />

                {/* Ativação do perfil profissional para quem já tem conta -- o mesmo funil do
                    cadastro novo, entrando pelo meio. Antes de existir, o cliente que quisesse
                    trabalhar caía no /usuario/novo pelos CTAs da Home e travava no checkcpf. */}
                <Route path="/profissional/ativar"	element={
                    <PrivateRoute>
                        <AtivarPerfilProfissional />
                    </PrivateRoute>
                } />

                <Route path="/dashboard" element={
                    <PrivateRoute>
                        <Dashboard />
                    </PrivateRoute>
                    }
                />

                {/* Área administrativa. As seções são rotas filhas do shell (pages/Admin), que
                    desenha a navegação e o Outlet -- assim o financeiro futuro entra como mais um
                    filho, sem duplicar casca. */}
                {telasAdmin && (
                <Route path="/admin" element={
                    <AdminRoute>
                        <telasAdmin.Admin />
                    </AdminRoute>
                }>
                    <Route index				element={<Navigate to="/admin/painel" replace />} />
                    <Route path="painel"		element={<telasAdmin.Painel />} />
                    <Route path="metas"			element={<telasAdmin.Metas />} />
                    <Route path="usuarios"		element={<telasAdmin.Usuarios />} />
                    <Route path="usuarios/mapa"	element={<telasAdmin.UsuariosMapa />} />
                    <Route path="servicos"		element={<telasAdmin.Servicos />} />
                    <Route path="demanda"		element={<telasAdmin.Demanda />} />
                    <Route path="verificacoes"	element={<telasAdmin.Verificacoes />} />
                    <Route path="ocorrencias"	element={<telasAdmin.Ocorrencias />} />
                    <Route path="agendamentos"	element={<telasAdmin.Agendamentos />} />

                    {/* Financeiro em abas: o catálogo de planos e o extrato de pagamentos recebidos. */}
                    <Route path="financeiro"	element={<telasAdmin.Financeiro />}>
                        <Route index			element={<Navigate to="/admin/financeiro/planos" replace />} />
                        <Route path="planos"	element={<telasAdmin.Planos />} />
                        <Route path="extrato"	element={<telasAdmin.Extrato />} />
                    </Route>
                    {/* Mesmo arranjo do Financeiro: abas sob /admin/configuracoes — a de edição (gerais), a de
                        referência (regras-dos-planos) e a lista dos selos Fundador(a) (fundadores, C6). */}
                    <Route path="configuracoes"	element={<telasAdmin.Configuracoes />}>
                        <Route index			element={<Navigate to="/admin/configuracoes/gerais" replace />} />
                        <Route path="gerais"	element={<telasAdmin.ConfiguracoesGerais />} />
                        <Route path="regras-dos-planos" element={<telasAdmin.RegrasDosPlanos />} />
                        <Route path="fundadores"	element={<telasAdmin.Fundadores />} />
                    </Route>
                    <Route path="health"		element={<telasAdmin.Health />} />
                    {/* Aba Log da Saúde. Rota irmã, e não filha: o AdminRoute só abre as exceções
                        da Saúde (sem sessão, API fora, tela estreita) para /admin/health exato, e o
                        log mostra dado — segue a regra das outras seções. */}
                    <Route path="health/log"	element={<telasAdmin.HealthLog />} />
                    {/* Aba Jobs automatizados da Saúde: irmã do Log, pela mesma razão. */}
                    <Route path="health/jobs"	element={<telasAdmin.HealthJobs />} />
                </Route>
                )}

                {/* No build do app não há admin: quem chegar a /admin (ou às rotas antigas, que
                    levavam a ele) vai para o painel, o mesmo destino de quem não é administrador. */}
                {!telasAdmin && (
                <Route path="/admin/*"			element={<Navigate to="/dashboard" replace />} />
                )}

                {/* Rotas administrativas antigas. Ficam como redirecionamento porque estão em
                    favoritos e em links antigos -- e porque as duas eram protegidas só por
                    PrivateRoute, então qualquer pessoa logada chegava nelas pela URL. Apontam para
                    /admin nos dois builds: no do app, a rota acima leva ao painel. */}
                <Route path="/servico"			element={<Navigate to="/admin/servicos" replace />} />
                <Route path="/usuario/relatorio"	element={<Navigate to="/admin/usuarios" replace />} />

                <Route path="/usuario/novo"		element={
                    <UsuarioNovo />
                    }
                />
                <Route path="/usuario/editar"	element={
                    <PrivateRoute>
                        <UsuarioEditar />
                    </PrivateRoute>
                    }
                />
                <Route path="/usuario/despedida" element={
                    <PrivateRoute>
                        <UsuarioDespedida />
                    </PrivateRoute>}
                />

                <Route path="/assinatura/pagamento" element={
                    <PrivateRoute>
                        <PagamentoAssinatura />
                    </PrivateRoute>}
                />

                {/* Envio do documento. Chega aqui de dois lugares: a confirmação do Pix, logo depois
                    do cadastro, e o aviso de pendência do dashboard. A mesma tela também mostra o
                    estado da análise -- é a resposta para "e aí, já saiu?". */}
                <Route path="/verificacao/documento" element={
                    <PrivateRoute>
                        <Verificacao />
                    </PrivateRoute>}
                />

                <Route path="/quemsomos"	element={<QuemSomos />} />
                <Route path="/perguntas-frequentes" element={<PerguntasFrequentes />} />

                {/* Central de ajuda: o passo a passo das telas, um artigo por tarefa. Pública,
                    como o FAQ, para o Google e para o atendimento mandar o link a quem não entrou.
                    Os artigos estão no sitemap (npm run gerar:sitemap). */}
                <Route path="/ajuda" element={<Ajuda />} />
                <Route path="/ajuda/:slug" element={<ArtigoAjuda />} />
                <Route path="/politica-de-cookies" element={<PoliticaCookies />} />
                <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
                <Route path="/termos-de-uso" element={<TermosUso />} />

                {/* Pública e sem login de propósito: o Google exige uma URL de solicitação de
                    exclusão acessível de FORA do aplicativo, e ela precisa servir justamente a
                    quem perdeu o acesso à conta -- que é quem não consegue usar o botão de
                    dentro dela. */}
                <Route path="/excluir-conta" element={<ExclusaoDeConta />} />

                {/* Uma página por cidade para o Google ("faxineira jacarezinho"). Pública porque
                    o robô não tem sessão; quem tem é levado direto à busca na cidade. As URLs
                    estão no sitemap (npm run gerar:sitemap). O índice /diaristas lista as mesmas
                    cidades do sitemap: sem link nenhum para elas, o Google não as indexava. */}
                <Route path="/diaristas" element={<Diaristas />} />
                <Route path="/diaristas/:slug" element={<Cidade />} />

                <Route path="/manutencao"	element={<Manutencao />} />

                {/* Curinga, por último: o <Routes> escolhe a rota mais específica, não a primeira,
                    mas manter path="*" no fim deixa a leitura do arquivo igual à precedência.
                    Sem ela uma URL desconhecida não casava com nada e a tela ficava vazia entre a
                    ausência de NavBar e o rodapé. */}
                <Route path="*"			element={<NaoEncontrada />} />

            </Routes>
            </Suspense>

            {/* No mobile o rodapé completo só aparece na Home: nas telas internas ele
                virava uma parede de conteúdo repetido no fim de cada página, num
                contexto em que o usuário está executando uma tarefa e não navegando.
                Os links institucionais e os canais de atendimento continuam a um
                toque de distância pelo menu sanduíche (ver NavBar.jsx). No lugar deles
                fica a assinatura da marca em uma linha — antes ali não ficava nada, e
                a página terminava no último card, com cara de conteúdo cortado. De md
                pra cima — mesmo breakpoint em que o NavBar troca hambúrguer por menu
                horizontal — nada muda. Corte via CSS, não via JS: media query não
                erra o primeiro render nem provoca reflow.

                /admin entra por prefixo, e não na lista acima: a lista é de comparação exata e a
                área administrativa tem uma seção por rota filha, então cada seção nova precisaria
                ser lembrada ali -- e o rodapé voltaria a aparecer justamente na tela recém-criada.

                No app o rodapé não existe em tela nenhuma, nem a assinatura, nem em tablet: a
                Home não abre lá (ver RaizDoApp), os links institucionais já estão na gaveta Ajuda
                do menu, e a assinatura com a versão passa a fechar o próprio menu sanduíche (ver
                NavBar.jsx) -- um rodapé por tela, num app, é peso repetido no fim de cada uma.
                `ehNativo` é síncrono, então não há lampejo do rodapé antes de ele sumir.

                A exceção é o Login, e só no app: é a primeira tela de quem acabou de instalar, sem
                NavBar e portanto sem gaveta Ajuda -- sem o rodapé, a pessoa não chega a Quem somos,
                FAQ, termos ou atendimento antes de ter conta. Ele entra sem o que no app não se
                aplica (ver `noApp` no Footer) e, no celular, resumido à assinatura como nas telas
                internas: completo, ele pesava mais que o próprio formulário de entrada. Os Termos e
                a Política seguem a um toque pelo aviso do próprio Login; em tablet o rodapé
                continua inteiro. Na web o Login segue sem rodapé: lá o "Voltar à tela inicial"
                leva à Home, que tem tudo isso. */}
            {rodapeDoLoginNoApp ? (
                <Footer noApp resumidoNoMobile />
            ) : !semRodape && (
                <Footer resumidoNoMobile={pathname !== '/'} />
            )}

            {/* O componente existia desde a Política de Cookies, mas nenhum arquivo o importava --
                ou seja, o banner nunca chegou a aparecer e o consentimento nunca era pedido. Entra
                aqui, fora do bloco do rodapé, porque ele é fixo na viewport e vale para toda tela,
                inclusive as de fluxo focado que não têm rodapé. */}
            <CookieBanner />

            {/* A mesma pergunta do CookieBanner, com o texto do aplicativo. Cada uma cuida da sua
                superfície -- o banner devolve null no app, esta devolve null na web -- e as duas
                gravam a MESMA decisão, então há uma resposta por aparelho, não uma por superfície. */}
            <ConsentimentoApp />

            {/* Faixa "sem conexão", fixa no rodapé enquanto o aparelho não tem rede. Aqui, e não
                em cada página, pelo mesmo motivo do CookieBanner: vale para toda tela, inclusive as
                de fluxo focado -- foi no cadastro, em modo avião, que a falta dela apareceu. */}
            <AvisoSemConexao />
        </>
    );
};

// O script do reCAPTCHA (carregado pelo Login, veja src/pages/Login.jsx)
// dispara timers internos que podem estourar erro bem depois de o usuário já
// ter navegado pra outra tela (ex: "Timeout (b)" ao tentar falar com o
// Google). Não é um erro da nossa aplicação, então evitamos que ele vire um
// "uncaught error" — o que no dev server do CRA aparece como overlay
// vermelho travando a tela.
const isRecaptchaNoise = (text) =>
    typeof text === 'string' && text.toLowerCase().includes('recaptcha');

const useSuppressRecaptchaNoise = () => {
    useEffect(() => {
        const handleWindowError = (event) => {
            if (isRecaptchaNoise(event.filename) || isRecaptchaNoise(event.message)) {
                event.preventDefault();
            }
        };

        const handleUnhandledRejection = (event) => {
            const reason = event.reason;
            if (isRecaptchaNoise(`${reason?.message || ''} ${reason?.stack || ''}`)) {
                event.preventDefault();
            }
        };

        window.addEventListener('error', handleWindowError);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        return () => {
            window.removeEventListener('error', handleWindowError);
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, []);
};

const App = () => {
    useSuppressRecaptchaNoise();

    return (
        <Router>
            <BackendStatusProvider>
                <AppRoutes />
            </BackendStatusProvider>
            {/* A abertura do app, por cima de tudo -- inclusive da tela de manutenção, que é um
                early return lá dentro do AppRoutes. Na web devolve null. */}
            <SplashApp />
            {/* O pedido de atualização do app. Aqui, e não no AppRoutes, pelo mesmo motivo da abertura:
                a tela de manutenção é um early return lá dentro. Na web devolve null. */}
            <AvisoVersaoApp />
        </Router>
    );
};

export default App;
